import clone from 'lodash/clone';
import pluralize from 'pluralize';
import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistory from '../../components/GridHistory';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearch from '../../components/ListSearch';
import ListSearchMinimalContentLoader from '../../components/ListSearchMinimalContentLoader';
import NavSwitcher from '../../components/NavSwitcher';
import { ENDPOINT } from '../../constants/endpoints';
import {
  GLOBAL_SEARCH_DATA_TYPE,
  GLOBAL_SEARCH_SECTION,
} from '../../constants/global-search';
import { ROUTE } from '../../constants/route';
import { updateHistory } from '../../helpers/grid-history';
import { Role } from '../../helpers/user';
import withProcessApplyAction from '../../hocs/withProcessApplyAction';
import { useApi } from '../../hooks/useApi';
import { useGrid } from '../../hooks/useGrid';
import { useReviewAction } from '../../hooks/useReviewAction';
import PageLayout from '../../layouts/PageLayout';
import ApplyJobConfirmDialog from '../../scenes/Dialog/components/ApplyJobConfirmDialog';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import List from '../../scenes/List';
import FirmItem from '../../scenes/List/components/FirmItem';
import InboxItem from '../../scenes/List/components/InboxItem';
import JobsItem from '../../scenes/List/components/JobsItem';
import NetworkItem from '../../scenes/List/components/NetworkItem';
import ReviewItem from '../../scenes/List/components/ReviewItem';
import Sidebar from '../../scenes/Sidebar';
import GlobalSearchFilters from '../../scenes/Sidebar/components/GlobalSearchFilters';
import { action } from './actions';
import './styles.css';

const GlobalSearch = props => {
  const {
    user,
    history,
    globalActions: { updateUserResume },
    applyActions: { apply },
    globalOptions,
  } = props;

  const {
    rate: rateReview,
    view: handleClickReviewLink,
    message: handleClickSendMessage,
    isAllowedRate: handleBeforeReviewRatingUpdate,
  } = useReviewAction(user, globalOptions);

  useEffect(() => {
    if (
      !history.location.state ||
      ((!history.location.state.initialParams ||
        !history.location.state.initialParams.search) &&
        (!history.location.state.gridParams ||
          !history.location.state.gridParams.search))
    ) {
      history.replace(ROUTE.ERROR_404);
    }
  }, []);

  const grid = useGrid(ENDPOINT.COLLECTION.GLOBAL_SEARCH, params => {
    updateHistory(history, params);
  });

  const [isCandidate, isRecruiter] = useMemo(
    () => [Role.isCandidate(user), Role.isRecruiter(user)],
    [user]
  );

  const api = useApi();

  const [activeView, setActiveView] = useState('both');

  const navSwitcherItems = useMemo(
    () =>
      [
        { title: 'All', param: 'both' },
        { title: 'Interview Reviews', param: 'interview' },
        { title: 'Employee Reviews', param: 'review' },
        { title: 'Company Reviews', param: 'overview' },
      ].map(item =>
        item.param === activeView ? { ...item, selected: true } : item
      ),
    [activeView]
  );

  const sortControls = useMemo(() => {
    if (GLOBAL_SEARCH_SECTION.JOB === grid.data.params.section) {
      return [
        {
          defaultCaption: 'Most Recent',
          toggledCaption: 'Most Recent',
          value: 'recent',
        },
        {
          defaultCaption: 'Start Date',
          toggledCaption: 'start date',
          value: 'start date',
        },
      ];
    }
    if (GLOBAL_SEARCH_SECTION.REVIEW === grid.data.params.section) {
      return [
        {
          defaultCaption: 'Most Recent',
          toggledCaption: 'Most Recent',
          value: 'recent',
        },
        {
          defaultCaption: 'Most Useful',
          toggledCaption: 'Most Useful',
          value: 'useful',
        },
      ];
    }
    if (GLOBAL_SEARCH_SECTION.FIRM === grid.data.params.section) {
      return [
        {
          defaultCaption: 'Most Recent',
          toggledCaption: 'Most Recent',
          value: 'recent',
        },
        {
          defaultCaption: 'Rating',
          toggledCaption: 'Rating',
          value: 'firm rating',
        },
      ];
    }

    return [
      {
        defaultCaption: 'Category',
        toggledCaption: 'Category',
        value: 'default',
      },
      {
        defaultCaption: 'Most Recent',
        toggledCaption: 'Most Recent',
        value: 'recent',
      },
    ];
  }, [grid.data]);

  // ---- network actions

  const onCandidateStatusChange = (id, status, data = {}) => {
    grid.actions.setDataRows(
      grid.data.rows.map(item =>
        item.CandidateID === id
          ? { ...item, ConnectStatus: status, ...data }
          : item
      )
    );
  };

  // ---- inbox actions
  const hasAccess = (showMessage = true) => {
    return isRecruiter || Role.isCandidate(user, showMessage);
  };

  const handleMarkAsRead = (message, isOpen = true) => {
    if (hasAccess(false)) {
      action.inbox.markAsRead(api, { message, isOpen }, { grid });
    }
  };

  const onDelete = message => {
    if (hasAccess()) {
      action.inbox.remove(api, { message }, { grid });
    }
  };

  const onUndelete = message => {
    if (hasAccess()) {
      action.inbox.restore(api, { message }, { grid });
    }
  };

  const onDeletePermanently = message => {
    if (hasAccess()) {
      action.inbox.removePermanently(api, { message }, { grid });
    }
  };

  const onReply = message => {
    if (hasAccess()) {
      action.inbox.reply(api, { message }, { grid });
    }
  };

  const onForward = message => {
    if (hasAccess()) {
      action.inbox.forward(api, { message }, { grid });
    }
  };

  const onResumeAccept = message => {
    if (hasAccess()) {
      action.inbox.resumeAccept(api, { message }, { grid });
    }
  };

  const onResumeReject = message => {
    if (hasAccess()) {
      action.inbox.resumeReject(api, { message }, { grid });
    }
  };

  const onConnectAccept = message => {
    if (hasAccess()) {
      action.inbox.connectAccept(api, { message }, { grid });
    }
  };

  const onConnectReject = message => {
    if (hasAccess()) {
      action.inbox.connectReject(api, { message }, { grid });
    }
  };

  // ---- review actions

  const handleReviewRatingUpdate = (review, value) =>
    rateReview(review, value).then(response =>
      grid.actions.setDataRows(
        grid.data.rows.map(row =>
          row.ReviewID === response.ReviewID ? { ...row, ...response } : row
        )
      )
    );

  // job actions

  const onViewStatistics = job =>
    action.job.viewStatistics(api, history, user, { job });

  // const onApply = job =>
  //   action.job.apply(api, history, user, { job }, { grid });

  const onApply = process => {
    const onSuccessApply = () => {
      grid.actions.setDataRows(
        grid.data.rows.map(row =>
          row.ProcessID === process.ProcessID
            ? {
                ...row,
                ApplicationProcessID: true,
              }
            : row
        )
      );
    };

    ApplyJobConfirmDialog({ id: process.Id })
      .then(() => {
        apply(process).then(success => {
          if (success) {
            onSuccessApply();
            BasicDialog({
              bodyRender: `Thank you! A recruiter will be notified that you are interested in ${process.PositionTitle} Role and get in touch with you soon.`,
            });
          }
        });
      })
      .catch(() => {
        apply(process, false).then(success => {
          if (success) {
            onSuccessApply();
            BasicDialog({
              bodyRender: `A recruiter will be notified that you are not interested in ${process.PositionTitle} Role.`,
            });
          }
        });
      });
  };

  const onApplyCheck = job =>
    action.job.checkApply(api, history, user, { job }, { updateUserResume });

  // ---- firm actions

  const handleFirmRatingUpdate = (firm, value) =>
    action.firm.setRating(
      api,
      user,
      {
        id: firm.FirmID,
        mark: value,
      },
      { grid }
    );

  const handleBeforeFirmRatingUpdate = () =>
    action.firm.beforeSetRating(api, user);

  const handleClickFirmEmployee = firm =>
    action.firm.clickEmployeeLink(history, user, { firmName: firm.FirmName });

  const items = useMemo(
    () =>
      grid.data.rows.map(row => {
        let record = null;
        switch (row.DataType) {
          case GLOBAL_SEARCH_DATA_TYPE.CANDIDATE:
            record = (
              <NetworkItem
                user={user}
                key={row.CandidateID}
                item={row}
                hasSelectMarker={false}
                actions={{
                  onStatusChangeHandler: onCandidateStatusChange,
                }}
              />
            );
            break;

          case GLOBAL_SEARCH_DATA_TYPE.FIRM:
            record = (
              <FirmItem
                key={row.FirmID}
                item={row}
                actions={{
                  handleRatingUpdate: handleFirmRatingUpdate,
                  handleBeforeRatingUpdate: handleBeforeFirmRatingUpdate,
                  onClickEmployeeHandler: handleClickFirmEmployee,
                }}
              />
            );
            break;

          case GLOBAL_SEARCH_DATA_TYPE.MESSAGE:
            record = (
              <InboxItem
                key={row.MessageID}
                user={user}
                item={row}
                actions={{ onRead: handleMarkAsRead }}
                footerActions={{
                  Inbox: [
                    {
                      caption: 'Reply',
                      icon: 'reply',
                      handler: onReply,
                    },
                    {
                      caption: 'Delete',
                      icon: 'delete',
                      handler: onDelete,
                    },
                    {
                      caption: 'Forward',
                      icon: 'forward',
                      handler: onForward,
                    },
                  ],
                  Sent: [
                    {
                      caption: 'Forward',
                      icon: 'forward',
                      handler: onForward,
                    },
                    {
                      caption: 'Delete',
                      icon: 'delete',
                      handler: onDelete,
                    },
                  ],
                  Trash: [
                    {
                      caption: 'Restore',
                      icon: 'reply',
                      handler: onUndelete,
                    },
                    {
                      caption: 'Delete',
                      icon: 'delete',
                      handler: onDeletePermanently,
                    },
                  ],
                  Connect: [
                    {
                      caption: 'Accept',
                      icon: 'accept',
                      handler: onConnectAccept,
                    },
                    {
                      caption: 'Reject',
                      icon: 'reject',
                      handler: onConnectReject,
                    },
                  ],
                  Resume: [
                    {
                      caption: 'Accept',
                      icon: 'accept',
                      handler: onResumeAccept,
                    },
                    {
                      caption: 'Reject',
                      icon: 'reject',
                      handler: onResumeReject,
                    },
                  ],
                }}
              />
            );
            break;

          case GLOBAL_SEARCH_DATA_TYPE.INTERVIEW:
          case GLOBAL_SEARCH_DATA_TYPE.OVERVIEW:
          case GLOBAL_SEARCH_DATA_TYPE.REVIEW:
            record = (
              <ReviewItem
                key={row.ReviewID}
                item={row}
                actions={{
                  handleReviewRatingUpdate,
                  handleClickReviewLink,
                  handleBeforeReviewRatingUpdate,
                  handleClickSendMessage,
                }}
              />
            );
            break;

          case GLOBAL_SEARCH_DATA_TYPE.PROCESS:
            record = (
              <JobsItem
                item={row}
                key={row.ProcessID}
                actions={{
                  onApply,
                  onApplyCheck,
                  onViewStatistics,
                }}
              />
            );
            break;

          default:
            return record;
        }

        return record;
      }),
    [grid.data.rows]
  );

  const onChangeSectionHandler = section => {
    const params = history.location.state.gridParams
      ? history.location.state.gridParams
      : history.location.state.initialParams;
    const nextParams = clone({ ...params, section, sortBy: 'recent' });
    delete nextParams.page;
    grid.actions
      .fetch(nextParams)
      .then(params => updateHistory(history, params));
  };

  const onChangeReviewViewHandler = item => {
    const nextParams = clone({ ...grid.data.params, view: item.param });
    grid.actions.fetch(nextParams).then(() => setActiveView(item.param));
  };

  return (
    <GridHistory
      history={history}
      grid={grid}
      initialParams={{ sortBy: 'recent', section: 'all' }}
    >
      <PageLayout
        pageClass="global-search-page"
        pageTitle="Search for contacts, firms and reviews"
        mainContent={
          <>
            {grid.data.init ? (
              <ListSearch
                renderHeader={<b>{grid.data.params.search}</b>}
                hideSearchInput
                sortBy={grid.data.params.sortBy}
                sortControls={sortControls}
                recordsCount={grid.data.rowsCount}
                recordsName={pluralize('result', Number(grid.data.rowsCount))}
                onChangeSortByHandler={grid.actions.sortBy}
                onSendSearchQueryHandler={grid.actions.search}
                isFetching={grid.data.isFetching}
              >
                {grid.data.params.section === GLOBAL_SEARCH_SECTION.REVIEW ? (
                  <NavSwitcher
                    items={navSwitcherItems}
                    onChange={onChangeReviewViewHandler}
                  />
                ) : null}
              </ListSearch>
            ) : (
              <ListSearchMinimalContentLoader />
            )}

            <div className="global-search-page__list">
              <div className="horizontal-loader__container">
                {grid.data.isFetching && <HorizontalLoader />}
              </div>

              <List
                items={items}
                itemRender={item => item}
                actions={{
                  onChangePage: grid.actions.readPage,
                }}
                pagination={{
                  page: grid.data.page,
                  count: grid.data.rowsCount,
                  perPage: grid.data.perPage,
                }}
              />
            </div>
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              <GlobalSearchFilters
                restricted={!isCandidate}
                onClick={onChangeSectionHandler}
                selected={grid.data.params.section}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistory>
  );
};

export default compose(
  withProcessApplyAction
)(GlobalSearch);
